import React, { useState, useRef, useEffect, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import Header from "components/Header";
import MapGrid from "components/MapGrid/MapGrid";
import TabSlider from "components/TabSlider";
import SwipeableInteresPoints from "components/MapGrid/SwipeableInteresPoints";
import Loading from "components/Loading";

import useLandingSession from "hooks/useLandingSession";
import { useInterestPoints, useCategories, useMap } from "graphql/useMaps";
import { useMapDataGrid } from "components/MapGrid/hooks";

import history from "routes/history";
import { useStats, persistStats } from "contexts/stats";

const SingleMap = () => {
    const { id, mainMapId, depth } = useParams();
    const headerRef = useRef();
    const mapContainerRef = useRef(null);

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const path = useRef("");
    const loc = useLocation();

    const [activeCategory, setActiveCategory] = useState("all");
    const [filteredInterestPoints, setFilteredInterestPoints] = useState([]);
    const stats = useStats();

    const { getInterestPoints, loading: loadingInterestPoints, interestPoints } = useInterestPoints();
    const { getCategories, loading: loadingCategories, categories } = useCategories();
    const { getMap, loading: loadingMap, map } = useMap();
    const { gridPoints, gridCategories, activePoint, setActivePoint } = useMapDataGrid(interestPoints, categories);
    const mapGridRef = useRef(null);
    const { landing } = useLandingSession();

    const designColors = landing?.theme?.colors;
    const editorColors = landing?.mapsStyles;

    useEffect(() => {
        getInterestPoints({ variables: { mapID: id, page: 1, size: 100 } });
        getCategories({ variables: { mapID: id, page: 1, size: 100 } });
        getMap({ variables: { id } });
    }, []);

    useEffect(() => {
        const setVh = () => {
            const viewportHeight = window.visualViewport?.height || window.innerHeight;
            const vh = viewportHeight * 0.01;
            document.documentElement.style.setProperty("--vh", `${vh}px`);
        };

        setVh();
        window.addEventListener("resize", setVh);
        window.addEventListener("orientationchange", setVh);
        window.visualViewport?.addEventListener("resize", setVh);
        window.visualViewport?.addEventListener("scroll", setVh);

        return () => {
            window.removeEventListener("resize", setVh);
            window.removeEventListener("orientationchange", setVh);
            window.visualViewport?.removeEventListener("resize", setVh);
            window.visualViewport?.removeEventListener("scroll", setVh);
        };
    }, []);

    useEffect(() => {
        if (path.current == "") {
            path.current = loc.pathname;
        } else if (loc.pathname != path.current) {
            location.reload();
            path.current = loc.pathname;
        }
    }, [loc]);

    useEffect(() => {
        const filtered = getFilteredInterestPoints(interestPoints, categories, activeCategory);

        setFilteredInterestPoints(filtered);
        setActivePoint(filtered?.[0]?.id);
    }, [interestPoints, activeCategory]);

    const categoryOptions = useMemo(() => {
        const defaultOption = {
            value: "all",
            label: t("all"),
            onClick: () => {
                setActiveCategory("all");
                setTimeout(() => {
                    mapGridRef.current?.zoomToElement("largest-cluster");
                }, 200);
            },
        };
        const otherOptions = categories.map((cat) => ({
            value: cat.id,
            label: cat.name,
            onClick: () => {
                setActiveCategory(cat.id);
                setTimeout(() => {
                    mapGridRef.current?.zoomToElement("largest-cluster");
                }, 200);
            },
        }));
        return [defaultOption, ...otherOptions];
    }, [categories]);

    const mapData = useMemo(() => {
        if (map) {
            return {
                id: map.id,
                name: map.name,
                image: map.image,
            };
        }
        return null;
    }, [map]);

    const loading = loadingInterestPoints || loadingCategories || loadingMap;

    return (
        <section
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                height: "100%",
                overflow: "hidden",
                WebkitOverflowScrolling: "auto",
                overscrollBehavior: "none",
            }}
            className="basic has-top"
        >
            {loading && <Loading />}
            <Header
                ref={headerRef}
                title={mapData?.name}
                mainMap={Number(depth) >= 2 && !!mainMapId && id !== mainMapId}
                backAction={() => {
                    if (!mainMapId) {
                        persistStats({ stats, eventName: "Maps" });
                        window.location.href = "/#/site";
                        window.location.reload();
                    } else {
                        history.goBack();
                    }
                }}
                mainMapAction={getMainMapAction({ depth, mainMapId, history, location })}
                custom={{ styles: { ...(editorColors?.style?.header?.colors || {}) } }}
            />
            <div
                style={{
                    opacity: loading ? 0 : 1,
                    transition: "opacity 0.3s",
                }}
                className=" max-w-lg mx-auto h-full relative flex flex-col flex-1"
            >
                {categories.length > 1 && (
                    <TabSlider
                        className="h-14 mt-14"
                        selected={!activeCategory ? "all" : activeCategory}
                        selectedStyle={{
                            color:
                                editorColors?.style?.tabs?.status?.active?.colors?.fgColor ||
                                designColors?.activeForeground,
                            backgroundColor:
                                editorColors?.style?.tabs?.status?.default?.colors?.bgColor ||
                                designColors?.defaultBackground,
                        }}
                        notSelectedStyle={{
                            color:
                                editorColors?.style?.tabs?.status?.default?.colors?.fgColor ||
                                designColors?.defaultForeground,
                            backgroundColor:
                                editorColors?.style?.tabs?.status?.default?.colors?.bgColor ||
                                designColors?.defaultBackground,
                        }}
                        options={categoryOptions}
                    />
                )}
                <div
                    ref={mapContainerRef}
                    className={`relative bg-white  flex items-start justify-center ${
                        categories.length <= 1 ? "mt-14" : ""
                    }`}
                >
                    <MapGrid
                        mapData={mapData}
                        activeCategory={activeCategory}
                        setActiveCategory={setActiveCategory}
                        activePoint={activePoint}
                        setActivePoint={setActivePoint}
                        ref={mapGridRef}
                        categories={gridCategories}
                        interestPoints={gridPoints}
                    />
                </div>
                <SwipeableInteresPoints
                    anchorRef={mapContainerRef}
                    loading={loading}
                    generalStyles={{
                        bgColor: editorColors?.style?.details?.general?.colors?.bgColor || "#fff",
                        fgColor: editorColors?.style?.details?.general?.colors?.fgColor || "#000",
                        detail:
                            applyAlpha20(editorColors?.style?.details?.general?.colors?.fgColor) ||
                            "rgba(189, 200, 211, 0.2)",
                    }}
                    buttonStyles={{
                        bgColor: editorColors?.style?.details?.button?.colors?.bgColor,
                        fgColor: editorColors?.style?.details?.button?.colors?.fgColor,
                    }}
                    points={interestPoints}
                    active={activePoint}
                />
            </div>
        </section>
    );
};

export default SingleMap;

const getFilteredInterestPoints = (interestPoints, categories, activeCategory) => {
    if (!Array.isArray(interestPoints) || !interestPoints.length) return [];

    if (activeCategory === "all") return interestPoints;

    const category = categories.find((cat) => cat.id === activeCategory);
    if (!category?.mapCategoryInterestPoints?.length) return [];

    return category.mapCategoryInterestPoints
        .map((link) => interestPoints.find((point) => point.id === link.mapInterestPointID))
        .filter(Boolean);
};

const applyAlpha20 = (color) =>
    typeof color === "string"
        ? color.replace(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*[\d.]*\)/, "rgba($1, $2, $3, 0.2)")
        : color;

const getMainMapAction = ({ depth, mainMapId, history, location }) => {
    if (Number(depth) >= 2 && mainMapId) {
        return () => {
            history.push(`/#/maps/${mainMapId}`);
            location.reload();
        };
    }

    return undefined;
};
